import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Send, FileText, Mic, Globe, Trash2, PhoneForwarded } from "lucide-react";

const TYPE_ICONS = { pdf: FileText, voice_note: Mic, url: Globe, text: FileText };
const KB_ROLES = ["ai_knowledge_admin", "group_hr_head", "recruiter", "super_admin"];

export default function AgentChat() {
  const { user } = useAuth();
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => {
    const saved = localStorage.getItem("talentos_agent_session");
    if (saved) return saved;
    const sid = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("talentos_agent_session", sid);
    return sid;
  });
  const [kb, setKb] = useState([]);
  const [kbForm, setKbForm] = useState({ title: "", source_type: "pdf", content: "", url: "" });
  const [handoffs, setHandoffs] = useState([]);
  const bottomRef = useRef(null);

  const isKbAdmin = KB_ROLES.includes(user?.role);
  const isRecruiterSide = ["recruiter", "group_hr_head", "entity_hr_head", "super_admin"].includes(user?.role);

  useEffect(() => {
    api.get(`/agent/chat/${sessionId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    api.get("/agent/knowledge").then((r) => setKb(r.data)).catch(() => {});
    if (isRecruiterSide) api.get("/agent/handoffs").then((r) => setHandoffs(r.data)).catch(() => {});
  }, [sessionId, isRecruiterSide]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("talentos_token")}`,
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.token) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + ev.token };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      toast.error("Agent unavailable: " + e.message);
    } finally {
      setStreaming(false);
    }
  };

  const requestHandoff = async (channel) => {
    try {
      await api.post("/agent/handoff", { session_id: sessionId, channel, reason: "Candidate requested human recruiter" });
      toast.success(channel === "whatsapp" ? "Recruiter notified on WhatsApp (simulated) — they'll reach out shortly." : "Handoff created — a recruiter will join this conversation.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addKb = async () => {
    if (!kbForm.title) return toast.error("Title required");
    try {
      const { data } = await api.post("/agent/knowledge", kbForm);
      setKb((k) => [data, ...k]);
      setKbForm({ title: "", source_type: "pdf", content: "", url: "" });
      toast.success("Source indexed into the AI knowledge base");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delKb = async (id) => {
    await api.delete(`/agent/knowledge/${id}`);
    setKb((k) => k.filter((x) => x.id !== id));
  };

  const resolveHandoff = async (id) => {
    await api.post(`/agent/handoffs/${id}/resolve`);
    setHandoffs((h) => h.map((x) => (x.id === id ? { ...x, status: "resolved" } : x)));
  };

  return (
    <div data-testid="agent-page">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Intelligent Recruitment Agent</div>
      <h1 className="font-heading font-black text-3xl tracking-tight mt-1">AI Assistant</h1>

      <div className="flex gap-1 mt-5 border-b">
        {[["chat", "Chat"], ...(isKbAdmin ? [["kb", `Knowledge Base (${kb.length})`]] : []),
          ...(isRecruiterSide ? [["handoffs", `Handoffs (${handoffs.filter((h) => h.status === "pending").length})`]] : [])].map(([k, label]) => (
          <button key={k} data-testid={`agent-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors duration-150 ${tab === k ? "border-[#0A251D] text-[#0A251D]" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "chat" && (
        <div className="border border-t-0 bg-white">
          <div className="h-[480px] overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center pt-20">
                <div className="inline-block w-10 h-10 bg-[#0A251D] rotate-45 mb-4" />
                <div className="font-heading font-bold text-lg">Ask me anything about roles, benefits or the hiring process.</div>
                <div className="text-sm text-muted-foreground mt-1">Trained on the ICS Group knowledge base — PDFs, voice notes and the careers site.</div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#0A251D] text-white" : "bg-secondary"}`}>
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <div className="border-t p-4">
            <div className="flex gap-2">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} disabled={streaming}
                placeholder="e.g. What benefits does Fundle offer? What's the interview process?"
                className="flex-1 border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A251D]" />
              <button data-testid="chat-send-button" onClick={send} disabled={streaming}
                className="bg-[#0A251D] text-white px-5 hover:bg-[#133E31] transition-colors duration-150 disabled:opacity-60"><Send size={16} /></button>
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid="handoff-whatsapp-button" onClick={() => requestHandoff("whatsapp")}
                className="flex items-center gap-1.5 text-xs border px-3 py-1.5 hover:border-emerald-600 hover:text-emerald-700 transition-colors duration-150">
                <PhoneForwarded size={12} /> Talk to a recruiter on WhatsApp
              </button>
              <button data-testid="handoff-portal-button" onClick={() => requestHandoff("portal")}
                className="flex items-center gap-1.5 text-xs border px-3 py-1.5 hover:border-[#0A251D] hover:text-[#0A251D] transition-colors duration-150">
                <PhoneForwarded size={12} /> Continue with a human here
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "kb" && (
        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div className="border bg-white rounded-xl p-6" data-testid="kb-add-form">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-4">Add Knowledge Source</div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Type</label>
            <div className="flex gap-2 mt-1 mb-3">
              {[["pdf", "PDF"], ["voice_note", "Voice Note"], ["url", "Website URL"], ["text", "Text"]].map(([v, l]) => (
                <button key={v} onClick={() => setKbForm({ ...kbForm, source_type: v })}
                  className={`text-xs px-3 py-1.5 border transition-colors duration-150 ${kbForm.source_type === v ? "bg-[#0A251D] text-white border-[#0A251D]" : "hover:border-[#0A251D]"}`}>{l}</button>
              ))}
            </div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Title</label>
            <input data-testid="kb-title-input" value={kbForm.title} onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })} className="mt-1 mb-3 w-full border px-3 py-2 text-sm" />
            {kbForm.source_type === "url" && (
              <>
                <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">URL (crawl is simulated)</label>
                <input data-testid="kb-url-input" value={kbForm.url} onChange={(e) => setKbForm({ ...kbForm, url: e.target.value })} className="mt-1 mb-3 w-full border px-3 py-2 text-sm" placeholder="https://…" />
              </>
            )}
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
              {kbForm.source_type === "pdf" ? "PDF content (paste text)" : kbForm.source_type === "voice_note" ? "Transcript (or leave blank for simulated transcription)" : "Content"}
            </label>
            <textarea data-testid="kb-content-textarea" value={kbForm.content} onChange={(e) => setKbForm({ ...kbForm, content: e.target.value })} rows={5} className="mt-1 w-full border px-3 py-2 text-sm" />
            <button data-testid="kb-add-button" onClick={addKb}
              className="mt-3 bg-[#0A251D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Index Source</button>
          </div>
          <div className="border bg-white rounded-xl p-6">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-4">Indexed Sources</div>
            <div className="space-y-3">
              {kb.map((s) => {
                const Icon = TYPE_ICONS[s.source_type] || FileText;
                return (
                  <div key={s.id} className="flex items-start gap-3 border p-3" data-testid={`kb-source-${s.id}`}>
                    <Icon size={16} className="text-[#0A251D] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{s.title}</div>
                      <div className="text-xs text-muted-foreground capitalize">{s.source_type.replace("_", " ")} · {s.ingestion_status?.replace(/_/g, " ")} · by {s.created_by}</div>
                    </div>
                    <button onClick={() => delKb(s.id)} className="text-muted-foreground hover:text-red-600 transition-colors duration-150"><Trash2 size={14} /></button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "handoffs" && (
        <div className="border border-t-0 bg-white" data-testid="handoffs-panel">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-secondary/50 text-left">
              {["Candidate", "Channel", "Reason", "Status", ""].map((h) => <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {handoffs.map((h) => (
                <tr key={h.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">{h.candidate_name}</td>
                  <td className="px-4 py-3 capitalize">{h.channel}</td>
                  <td className="px-4 py-3 text-muted-foreground">{h.reason}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${h.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{h.status}</span></td>
                  <td className="px-4 py-3">
                    {h.status === "pending" && (
                      <button onClick={() => resolveHandoff(h.id)} className="text-xs text-[#0A251D] hover:underline">Mark resolved</button>
                    )}
                  </td>
                </tr>
              ))}
              {handoffs.length === 0 && <tr><td className="px-4 py-6 text-muted-foreground" colSpan={5}>No handoff requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
